import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { TopBar } from "@/components/TopBar";
import { inputCls } from "@/components/FormBits";
import { api, formatApiError, maskPhone } from "@/lib/api";
import { Landmark, ShieldCheck } from "lucide-react";
import { AdminMetrics } from "@/components/AdminMetrics";
import { AdminUserDirectory } from "@/components/AdminUserDirectory";

const fmtDate = (iso) => new Date(iso).toLocaleString("pt-BR");

const RequestRow = ({ r, onDone }) => {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (path, body) => {
    setBusy(true);
    try {
      await api.post(`/admin/password-requests/${r.id}/${path}`, body);
      toast.success(path === "reset" ? "Senha redefinida" : "Marcado como atendido");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const pending = r.status === "pendente";
  return (
    <li data-testid={`admin-request-${r.id}`} className="rounded-2xl border-2 border-slate-200 bg-white p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-heading text-xl font-bold text-emerald-950">{r.user_name || "Telefone sem conta"}</p>
          <p className="text-lg text-slate-700">{maskPhone(r.phone)} · {fmtDate(r.created_at)}</p>
          {r.message && <p className="mt-2 text-base text-slate-600">“{r.message}”</p>}
        </div>
        <span data-testid={`admin-request-status-${r.id}`} className={`rounded-full px-3 py-1 text-sm font-bold ${pending ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-900"}`}>
          {pending ? "Pendente" : "Atendido"}
        </span>
      </div>
      {pending && (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          {r.user_id && (
            <>
              <input data-testid={`admin-new-password-${r.id}`} className={inputCls} value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Nova senha (mín. 6)" />
              <button disabled={busy} onClick={() => run("reset", { new_password: pwd })} data-testid={`admin-reset-button-${r.id}`} className="min-h-[56px] shrink-0 rounded-xl bg-emerald-700 px-5 text-lg font-bold text-white hover:bg-emerald-800 disabled:opacity-60">
                Redefinir senha
              </button>
            </>
          )}
          <button disabled={busy} onClick={() => run("resolve")} data-testid={`admin-resolve-button-${r.id}`} className="min-h-[56px] shrink-0 rounded-xl border-2 border-slate-300 px-5 text-lg font-bold text-slate-800 hover:bg-slate-50">
            Marcar atendido
          </button>
        </div>
      )}
    </li>
  );
};

export default function Admin() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const load = useCallback(() => {
    setError("");
    api.get("/admin/password-requests").then((r) => setItems(r.data)).catch((e) => setError(formatApiError(e)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const loadPaymentSettings = useCallback(() => {
    api.get("/admin/payment-settings")
      .then((response) => setPaymentSettings(response.data))
      .catch((requestError) => toast.error(formatApiError(requestError)));
  }, []);

  useEffect(() => {
    loadPaymentSettings();
  }, [loadPaymentSettings]);

  const toggleAutomaticMode = async () => {
    if (!paymentSettings || (!paymentSettings.provider_configured && !paymentSettings.automatic_mode_enabled)) return;
    setPaymentBusy(true);
    try {
      const { data } = await api.patch("/admin/payment-settings", {
        automatic_mode_enabled: !paymentSettings.automatic_mode_enabled,
      });
      setPaymentSettings(data);
      toast.success(data.automatic_mode_enabled ? "Modo automático ativado." : "Modo manual reativado.");
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      setPaymentBusy(false);
    }
  };

  return (
    <div data-testid="admin-page" className="min-h-screen cofre-bg">
      <TopBar />
      <main className="mx-auto max-w-7xl px-5 py-10">
        <AdminMetrics />
        <section data-testid="admin-payment-settings" className="mb-10 rounded-3xl border-2 border-emerald-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-emerald-800">Integração futura</p>
              <h2 className="mt-1 flex items-center gap-2 font-heading text-2xl font-extrabold text-emerald-950"><Landmark className="h-6 w-6" />Pagamentos Pix automáticos</h2>
              <p className="mt-2 max-w-2xl text-lg text-slate-600">{paymentSettings?.automatic_mode_enabled ? "O modo automático está ativo e aguarda o adaptador do provedor para assumir confirmações." : "O Pix manual continua ativo até que um provedor seja configurado e este modo seja ativado por você."}</p>
            </div>
            <span data-testid="admin-payment-mode-status" className={`rounded-full px-3 py-2 text-sm font-bold ${paymentSettings?.automatic_mode_enabled ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"}`}>{paymentSettings?.automatic_mode_enabled ? "Automático ativo" : "Manual ativo"}</span>
          </div>
          <div className="mt-5 rounded-2xl bg-emerald-50 p-4">
            <p data-testid="admin-provider-status" className="flex items-center gap-2 font-bold text-emerald-950"><ShieldCheck className="h-5 w-5" />{paymentSettings?.provider_configured ? `Provedor configurado: ${paymentSettings.provider_name}` : "Nenhum provedor Pix configurado"}</p>
            {!paymentSettings?.provider_configured && <p className="mt-2 text-base text-slate-600">Quando um provedor for conectado no futuro, este botão poderá substituir a confirmação manual.</p>}
          </div>
          <button type="button" onClick={toggleAutomaticMode} disabled={paymentBusy || (!paymentSettings?.provider_configured && !paymentSettings?.automatic_mode_enabled)} data-testid="admin-payment-mode-toggle" className="mt-5 inline-flex min-h-[52px] items-center gap-2 rounded-xl bg-emerald-700 px-5 text-lg font-bold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"><ShieldCheck className="h-5 w-5" />{paymentSettings?.automatic_mode_enabled ? "Voltar ao modo manual" : "Ativar modo automático"}</button>
        </section>
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-emerald-950">Pedidos de nova senha</h2>
        <p className="mt-2 text-lg text-slate-600">Redefina a senha e avise a pessoa pelo WhatsApp.</p>
        {error && (
          <p data-testid="admin-requests-error" role="alert" className="mt-8 rounded-xl border-2 border-red-200 bg-red-50 px-4 py-3 text-lg text-red-800">
            Não foi possível carregar os pedidos: {error}
          </p>
        )}
        <ul className="mt-8 space-y-4" data-testid="admin-requests-list">
          {items?.length === 0 && <li data-testid="admin-requests-empty" className="text-lg text-slate-600">Nenhum pedido por enquanto.</li>}
          {items?.map((r) => <RequestRow key={r.id} r={r} onDone={load} />)}
        </ul>
        <AdminUserDirectory />
      </main>
    </div>
  );
}
